import { Alert, Anchor, Stack } from '@mantine/core';
import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { EvaluationHeader } from '@/module/Evaluation/components/EvaluationHeader';
import { getEvaluationOptions } from '@/module/Evaluation/model/EvaluationQuery';
import { AuthorEvaluationHeader } from '@/module/Evaluation/perRole/AuthorEvaluationHeader';
import { DepartmentAdminEvaluationHeader } from '@/module/Evaluation/perRole/DepartmentAdminEvaluationHeader';
import { DisciplineAdminEvaluationHeader } from '@/module/Evaluation/perRole/DisciplineAdminEvaluationHeader';
import { FacultyAdminEvaluationHeader } from '@/module/Evaluation/perRole/FacultyAdminEvaluationHeader';
import { UniversityAdminEvaluationHeader } from '@/module/Evaluation/perRole/UniversityAdminEvaluationHeader';

import { NoContentPlaceholder } from '@/core/components';

import { useCurrentUser } from '@feature/security';

const evaluationSearchSchema = z.object({
  userId: z.coerce.number().nullish(),
  disciplineId: z.coerce.number().nullish(),
  departmentId: z.coerce.number().nullish(),
  facultyId: z.coerce.number().nullish(),
});

export const Route = createFileRoute('/_auth/evaluation/_layout/$evaluationId/_layout')({
  validateSearch: evaluationSearchSchema,
  loader: async ({ params: { evaluationId }, context: { queryClient } }) => {
    const evaluation = await queryClient.ensureQueryData(getEvaluationOptions(evaluationId));

    return {
      evaluation,
      crumb: evaluation.title,
    };
  },
  component: RouteComponent,
});

function RouteComponent() {
  const { evaluationId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getEvaluationOptions(evaluationId));

  const { currentRole } = useCurrentUser();
  const hasAccess =
    ['AUTHOR', 'UNIVERSITY_ADMIN', 'DISCIPLINE_ADMIN', 'DEPARTMENT_ADMIN', 'FACULTY_ADMIN'].indexOf(currentRole) !== -1;

  if (!hasAccess) {
    return (
      <NoContentPlaceholder title="Nu aveți acces la acest ecran cu acest rol. Asigurați-vă că folosiți un rol de autor sau șef de disciplină / director departament sau decan" />
    );
  }

  return (
    <Stack>
      <Alert bg="#dedeff">
        <Anchor
          c="text"
          href="/fisa_evaluare_definitii_clarificari.pdf"
          target="_blank"
        >
          Pentru lista de definiții și clarificări, click aici
        </Anchor>
      </Alert>
      <EvaluationHeader evaluation={evaluation} />
      {currentRole === 'UNIVERSITY_ADMIN' && <UniversityAdminEvaluationHeader />}
      {currentRole === 'FACULTY_ADMIN' && <FacultyAdminEvaluationHeader />}
      {currentRole === 'DEPARTMENT_ADMIN' && <DepartmentAdminEvaluationHeader />}
      {currentRole === 'DISCIPLINE_ADMIN' && <DisciplineAdminEvaluationHeader />}
      {currentRole === 'AUTHOR' && <AuthorEvaluationHeader />}
    </Stack>
  );
}
